import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, CloudOff, Cloud, Loader2, Package, History, Camera, X, Calendar, Plus, Users, Save, Trash2, Search } from "lucide-react";
import exifr from "exifr";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatDateTime, formatTime } from "@/lib/dates";
import { enqueue, flushQueue, readQueue } from "@/utils/offline";
import Topbar from "@/components/Topbar";
import MyRecords from "@/components/collector/MyRecords";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";

const MATERIALS = [
  { v: "PET", label: "PET", desc: "Bottles, soft drink" },
  { v: "HDPE", label: "HDPE", desc: "Milk jugs, caps" },
  { v: "HDPE(N)", label: "HDPE(N)", desc: "Natural HDPE" },
  { v: "PP", label: "PP", desc: "Containers, tubs" },
  { v: "PP(N)", label: "PP(N)", desc: "Natural PP" },
  { v: "PE(N)", label: "PE(N)", desc: "Natural PE" },
  { v: "PE(M)", label: "PE(M)", desc: "Mixed PE" },
  { v: "UBC", label: "UBC", desc: "Used beverage cans" },
  { v: "Mixed Plastics", label: "Mixed Plastics", desc: "Mixed plastic types" },
];

function nowLocalIso() {
  // YYYY-MM-DDTHH:mm for datetime-local
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

async function getPhotoTimestamp(file) {
  try {
    const exif = await exifr.parse(file, ["DateTimeOriginal"]);
    if (exif?.DateTimeOriginal) {
      return { timestamp: exif.DateTimeOriginal.toISOString(), source: "exif" };
    }
  } catch (e) {
    // Falls back to "upload" timestamp below — EXIF is best-effort metadata.
    console.debug("exifr parse failed", e);
  }
  return { timestamp: new Date().toISOString(), source: "upload" };
}

export default function CollectorForm() {
  const [facilities, setFacilities] = useState([]);
  const [pickupAt, setPickupAt] = useState(nowLocalIso());
  const [siteName, setSiteName] = useState("");
  const [address, setAddress] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const [material, setMaterial] = useState("");
  const [weight, setWeight] = useState("");
  const [photos, setPhotos] = useState([]);
  const [materialRows, setMaterialRows] = useState([{ _uid: `mr-${Date.now()}`, material: "", weight: "" }]);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [agentModalMode, setAgentModalMode] = useState("manage");
  const [agentForm, setAgentForm] = useState({ name: "", address: "", phone: "", email: "", registration_number: "" });
  const [editAgentId, setEditAgentId] = useState(null);
  const [savingAgent, setSavingAgent] = useState(false);
  const [txAmount, setTxAmount] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPath, setProofPath] = useState("");
  const [proofFilename, setProofFilename] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(readQueue().length);
  // "records" = full submission history (Task 2/3/4). "new" = the collection
  // form. Recent-history widget is removed (Task 1) — records tab replaces it.
  const [tab, setTab] = useState("new");
  const [allRecords, setAllRecords] = useState([]);
  const [recordsSubTab, setRecordsSubTab] = useState("pending");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterAgent, setFilterAgent] = useState("__all__");
  const [filterRef, setFilterRef] = useState("");
  // Draft state (Task 5) — mirrors the processing / shipment draft pattern.
  const [draftList, setDraftList] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  // Resubmit mode: when the collector opens a rejected record via "Edit & Resubmit",
  // the whole form is pre-filled and the submit path POSTs to
  // /api/collections/{id}/resubmit instead of creating a new record.
  const [resubmitTarget, setResubmitTarget] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const upOn = () => setOnline(true);
    const upOff = () => setOnline(false);
    window.addEventListener("online", upOn);
    window.addEventListener("offline", upOff);
    return () => {
      window.removeEventListener("online", upOn);
      window.removeEventListener("offline", upOff);
    };
  }, []);

  useEffect(() => {
    api.get("/facilities").then((r) => {
      const list = r.data || [];
      setFacilities(list);
      // Auto-default when only one facility is in the collector's channel
      if (list.length === 1) setFacilityId(list[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/agents")
      .then(res => setAgents(res.data || []))
      .catch(() => {});
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");
  const resubmitId = searchParams.get("resubmit");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("agents") === "1") {
      setAgentModalMode("manage");
      setShowAgentModal(true);
      params.delete("agents");
      const newSearch = params.toString();
      navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useLiveRefresh(loadRecords, { intervalMs: 15000 });

  useEffect(() => {
    if (online && pendingCount > 0) {
      flushQueue().then(({ synced }) => {
        if (synced > 0) {
          toast.success(`${synced} offline collection${synced > 1 ? "s" : ""} synced`);
          setPendingCount(readQueue().length);
          loadRecords();
        }
      });
    }
  }, [online, pendingCount]);

  async function loadRecords() {
    try {
      const { data } = await api.get("/collections");
      setAllRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      console.debug("loadRecords failed", e);
    }
  }

  function openResubmit(rec) {
    if (!rec) return;
    setResubmitTarget({ id: rec.id, rejection_reason: rec.rejection_reason });
    setSiteName(rec.source_location || "");
    setAddress(rec.address || "");
    setAgentId(rec.agent_id || "");
    setFacilityId(rec.destination_facility_id || "");
    setReferenceNumber(rec.reference_number || "");
    if (rec.pickup_at) {
      const dt = new Date(rec.pickup_at);
      if (!isNaN(dt.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        setPickupAt(
          `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
        );
      }
    }
    const mats = Array.isArray(rec.materials) ? rec.materials : [];
    setMaterialRows(
      mats.length > 0
        ? mats.map((m, i) => ({
            _uid: `rs-${i}-${Math.random().toString(36).slice(2, 7)}`,
            material: m.material_type || "",
            weight: m.weight_kg == null ? "" : String(m.weight_kg),
          }))
        : rec.material_type
        ? [{ _uid: `rs-${Date.now()}`, material: rec.material_type, weight: String(rec.received_weight_kg || rec.estimated_weight_kg || "") }]
        : [{ _uid: `mr-${Date.now()}`, material: "", weight: "" }],
    );
    setPhotos(
      Array.isArray(rec.photos) && rec.photos.length > 0
        ? rec.photos.map((p, i) => ({
            idx: i,
            path: (p || {}).path || (p || {}).storage_path,
            preview: p?.path
              ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(p.path)}`
              : null,
            timestamp: (p || {}).timestamp,
            source: (p || {}).source || "upload",
            uploading: false,
          }))
        : rec.photo_path
        ? [{
            idx: 0,
            path: rec.photo_path,
            preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(rec.photo_path)}`,
            timestamp: null,
            source: "upload",
            uploading: false,
          }]
        : [],
    );
    if (rec.transaction_amount != null) setTxAmount(String(rec.transaction_amount));
    else setTxAmount("");
    if (rec.payment_proof_path) {
      setProofPath(rec.payment_proof_path);
      setProofFilename(rec.payment_proof_path.split("/").pop() || "proof");
    } else {
      setProofPath("");
      setProofFilename("");
    }
    setActiveDraft(null);
    setTab("new");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelResubmit() {
    setResubmitTarget(null);
    resetForm();
  }

  async function loadDrafts() {
    try {
      const { data } = await api.get("/collections/drafts");
      const list = Array.isArray(data) ? data : [];
      setDraftList(list);
      // Auto-resume when user clicked "Resume & edit" from records
      if (resumeId) {
        const match = list.find((x) => x.id === resumeId);
        if (match) resumeDraft(match);
      }
    } catch (e) {
      console.debug("loadDrafts failed", e);
    }
  }

  useEffect(() => {
    loadRecords();
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the collector followed a "Edit & Resubmit" link (?resubmit=<id>), open
  // that rejected record in the form once records are loaded.
  useEffect(() => {
    if (!resubmitId) return;
    if (resubmitTarget?.id === resubmitId) return;
    const match = allRecords.find((r) => r.id === resubmitId && r.status === "Rejected");
    if (match) openResubmit(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resubmitId, allRecords]);

  async function handlePhotoAdd(e) {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    const toAdd = files.slice(0, remaining);
    for (const file of toAdd) {
      const { timestamp, source } = await getPhotoTimestamp(file);
      const preview = URL.createObjectURL(file);
      const idx = Date.now() + Math.random();
      setPhotos(prev => [...prev, { idx, file, preview, timestamp, source, path: null, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const path = res.data?.path || res.data?.url || "";
        setPhotos(prev => prev.map(p => p.idx === idx ? { ...p, path, uploading: false } : p));
      } catch {
        setPhotos(prev => prev.filter(p => p.idx !== idx));
        toast.error("Photo upload failed");
      }
    }
  }

  function removePhoto(idx) {
    setPhotos(prev => prev.filter(p => p.idx !== idx));
  }

  function addMaterialRow() {
    if (materialRows.length < 6) setMaterialRows(prev => [...prev, { _uid: `mr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, material: "", weight: "" }]);
  }
  function removeMaterialRow(i) {
    if (materialRows.length > 1) setMaterialRows(prev => prev.filter((_, idx) => idx !== i));
  }
  function updateMaterialRow(i, field, value) {
    setMaterialRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  }

  async function saveAgent() {
    setSavingAgent(true);
    try {
      if (editAgentId) {
        await api.put(`/agents/${editAgentId}`, agentForm);
        setAgents(prev => prev.map(a => a.id === editAgentId ? { ...a, ...agentForm } : a));
      } else {
        const res = await api.post("/agents", agentForm);
        setAgents(prev => [...prev, res.data]);
      }
      setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" });
      setEditAgentId(null);
      toast.success(editAgentId ? "Agent updated" : "Agent added");
    } catch (e) {
      // Backend returns 409 + message when a collector tries to add a duplicate agent (same name).
      if (e?.response?.status === 409) {
        toast.error(formatApiError(e) || "A collection agent with this name already exists");
      } else {
        toast.error(formatApiError(e) || "Failed to save agent");
      }
    }
    finally { setSavingAgent(false); }
  }

  async function deleteAgent(id) {
    try {
      await api.delete(`/agents/${id}`);
      setAgents(prev => prev.filter(a => a.id !== id));
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  }

  async function uploadProof(file) {
    setProofUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofPath(res.data?.storage_path || res.data?.path || "");
      setProofFilename(file.name);
    } catch (e) {
      console.error("uploadProof failed", e);
      toast.error(formatApiError(e) || "Proof upload failed");
    }
    finally { setProofUploading(false); }
  }

  async function handleSubmit(e) {
    if (busy) return;
    setBusy(true);
    e.preventDefault();
    if (!siteName.trim() && !agentId) { setBusy(false); return toast.error("Site name is required"); }
    if (!address.trim()) { setBusy(false); return toast.error("Address is required"); }
    if (!facilityId) { setBusy(false); return toast.error("Select a destination facility"); }
    const validRows = materialRows.filter(r => r.material && parseFloat(r.weight) > 0);
    if (validRows.length === 0) { setBusy(false); return toast.error("Add at least one material with weight"); }
    if (photos.length === 0) { setBusy(false); return toast.error("At least one photo is required"); }
    if (photos.some(p => p.uploading)) { setBusy(false); return toast.error("Photos are still uploading…"); }
    // Reject future-dated pickups — a collection can only be logged for a
    // time that has already elapsed. Small clock-drift buffer (60s) is fine.
    const pickupDate = new Date(pickupAt);
    if (Number.isNaN(pickupDate.getTime())) {
      setBusy(false);
      return toast.error("Collection date is invalid");
    }
    if (pickupDate.getTime() > Date.now() + 60_000) {
      setBusy(false);
      return toast.error("Collection date can't be in the future");
    }

    // Build the payload up-front so we can reuse it for both the live POST
    // and the offline queue.
    const payload = {
      source_location: agents.find(a => a.id === agentId)?.name || siteName.trim(),
      agent_id: agentId || null,
      address: address.trim(),
      pickup_at: pickupDate.toISOString(),
      destination_facility_id: facilityId,
      materials: validRows.map(r => ({ material_type: r.material, weight_kg: parseFloat(r.weight) })),
      total_weight_kg: validRows.reduce((sum, r) => sum + parseFloat(r.weight || 0), 0),
      photos: photos.map(p => ({ path: p.path, timestamp: p.timestamp, source: p.source })),
      reference_number: referenceNumber.trim() || null,
      submitted_draft_id: activeDraft?.id || null,
    };
    if (txAmount && parseFloat(txAmount) > 0) {
      payload.transaction_amount = parseFloat(txAmount);
      payload.transaction_currency = "RM";
      if (proofPath) payload.payment_proof_path = proofPath;
    }

    // Offline path — as long as every photo has already finished uploading
    // (i.e. has a server-side `path`), we can safely queue the collection to
    // be POSTed when connectivity returns. If any photo is still in-flight
    // OR hasn't uploaded yet, keep blocking so we never queue a record with
    // no photo evidence.
    if (!navigator.onLine) {
      const allPhotosResolved = photos.every(p => p.path && !p.uploading);
      if (!allPhotosResolved) {
        setBusy(false);
        toast.error("Photo must upload before submitting. Try again when online.");
        return;
      }
      try {
        enqueue(payload);
        setPendingCount(readQueue().length);
        toast.success("Saved offline — will sync when connection returns");
        resetForm();
      } finally {
        setBusy(false);
      }
      return;
    }

    try {
      if (resubmitTarget?.id) {
        await api.post(`/collections/${resubmitTarget.id}/resubmit`, payload);
        toast.success("Collection resubmitted — awaiting facility review");
        setResubmitTarget(null);
        navigate({ pathname: location.pathname, search: "" }, { replace: true });
      } else {
        await api.post("/collections", payload);
        showSuccess("Collection submitted");
      }
      loadRecords();
      loadDrafts();
      resetForm();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  function resetForm() {
    setSiteName("");
    setAddress("");
    setFacilityId(facilities.length === 1 ? facilities[0].id : "");
    setAgentId("");
    setReferenceNumber("");
    setMaterialRows([{ _uid: `mr-${Date.now()}`, material: "", weight: "" }]);
    setPhotos([]);
    setTxAmount("");
    setProofFile(null);
    setProofPath("");
    setProofFilename("");
    setPickupAt(nowLocalIso());
    setActiveDraft(null);
  }

  function snapshotDraftPayload() {
    // Assemble the current form state into the draft payload shape. No
    // validation — saving partial/empty state is intentional.
    return {
      source_location: agents.find(a => a.id === agentId)?.name || siteName.trim(),
      address: address.trim(),
      pickup_at: pickupAt ? new Date(pickupAt).toISOString() : null,
      destination_facility_id: facilityId || null,
      agent_id: agentId || null,
      materials: materialRows
        .filter(r => r.material || r.weight)
        .map(r => ({
          material_type: r.material || null,
          weight_kg: r.weight === "" ? null : parseFloat(r.weight),
        })),
      total_weight_kg: materialRows.reduce((s, r) => s + (parseFloat(r.weight) || 0), 0) || null,
      photos: photos.map(p => ({ path: p.path, timestamp: p.timestamp, source: p.source })),
      reference_number: referenceNumber.trim() || null,
      transaction_amount: txAmount === "" ? null : parseFloat(txAmount),
      payment_proof_path: proofPath || null,
    };
  }

  async function saveDraft() {
    if (photos.some(p => p.uploading)) return toast.error("Photos are still uploading…");
    setSavingDraft(true);
    try {
      const payload = snapshotDraftPayload();
      let saved;
      if (activeDraft?.id) {
        const { data } = await api.patch(`/collections/drafts/${activeDraft.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/collections/drafts", payload);
        saved = data;
      }
      setActiveDraft({ id: saved.id, label: saved.label, updated_at: saved.updated_at });
      setDraftList(prev => [saved, ...prev.filter(x => x.id !== saved.id)]);
      toast.success(activeDraft?.id ? "Draft updated" : "Draft saved — you can resume it later.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDraft(false);
    }
  }

  function resumeDraft(d) {
    setSiteName(d.source_location || "");
    setAddress(d.address || "");
    setAgentId(d.agent_id || "");
    setFacilityId(d.destination_facility_id || "");
    setReferenceNumber(d.reference_number || "");
    if (d.pickup_at) {
      const dt = new Date(d.pickup_at);
      if (!isNaN(dt.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        setPickupAt(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
      }
    }
    const mats = Array.isArray(d.materials) ? d.materials : [];
    setMaterialRows(mats.length > 0
      ? mats.map((m, i) => ({
          _uid: `d-${i}-${Math.random().toString(36).slice(2, 7)}`,
          material: m.material_type || "",
          weight: m.weight_kg == null ? "" : String(m.weight_kg),
        }))
      : [{ _uid: `mr-${Date.now()}`, material: "", weight: "" }]
    );
    setPhotos(Array.isArray(d.photos) ? d.photos.map((p, i) => ({
      idx: i,
      path: p.path,
      preview: p?.path
        ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(p.path)}`
        : null,
      timestamp: p.timestamp,
      source: p.source || "upload",
      uploading: false,
    })) : []);
    // Restore transaction info so proof + amount don't get silently dropped.
    if (d.transaction_amount != null) setTxAmount(String(d.transaction_amount));
    else setTxAmount("");
    if (d.payment_proof_path) {
      setProofPath(d.payment_proof_path);
      setProofFilename(d.payment_proof_path.split("/").pop() || "proof");
    } else {
      setProofPath("");
      setProofFilename("");
    }
    setActiveDraft({ id: d.id, label: d.label, updated_at: d.updated_at });
    setTab("new");
    toast.success(`Resumed draft: ${d.label || "Untitled"}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function deleteDraftFromList(draftId) {
    if (!window.confirm("Discard this saved draft?")) return;
    try {
      await api.delete(`/collections/drafts/${draftId}`);
      setDraftList(prev => prev.filter(d => d.id !== draftId));
      if (activeDraft?.id === draftId) setActiveDraft(null);
      toast.success("Draft discarded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  function showSuccess(msg) {
    setSuccess(true);
    toast.success(msg);
    setTimeout(() => setSuccess(false), 1500);
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Topbar subtitle="Collection Agent" />
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Connectivity */}
        <div
          className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${
            online
              ? "bg-[#D2E4D6] text-[#056839]"
              : "bg-[#E4D5B7] text-[#A0522D]"
          }`}
          data-testid="connectivity-status"
        >
          <div className="flex items-center gap-2 font-medium">
            {online ? <Cloud className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}
            <span>{online ? "Online — submitting live" : "Offline — staging locally"}</span>
          </div>
          {pendingCount > 0 && (
            <span data-testid="pending-count" className="font-semibold">
              {pendingCount} pending
            </span>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488] mb-1">Quick collect</div>
          <h1 className="font-heading text-3xl font-semibold text-[#000000] leading-tight">
            Log a collection
          </h1>
          <p className="text-sm text-[#3F3F3F] mt-1">
            Snap a photo, enter the address &amp; weight.
          </p>
        </div>

        {/* Rejected — awaiting resubmission widget (visible only when at
            least one rejected record exists and we're not already in
            resubmit mode). Mirrors the Admin↔Facility resubmit banner. */}
        {(() => {
          const rejectedRecs = allRecords.filter((r) => r.status === "Rejected");
          if (rejectedRecs.length === 0 || resubmitTarget) return null;
          return (
            <Card
              className="border-[#A0522D] bg-[#FBEEE3] rounded-xl"
              data-testid="rejected-resubmit-widget"
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#A0522D] font-medium">
                      Awaiting resubmission
                    </div>
                    <div className="text-sm text-[#3F3F3F] mt-0.5">
                      {rejectedRecs.length === 1
                        ? "1 collection was rejected. Edit the details and resubmit for facility review."
                        : `${rejectedRecs.length} collections were rejected. Edit the details and resubmit for facility review.`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setTab("records"); setRecordsSubTab("rejected"); }}
                    data-testid="view-rejected-btn"
                    className="text-xs font-medium text-[#A0522D] hover:underline whitespace-nowrap"
                  >
                    View all →
                  </button>
                </div>
                <ul className="divide-y divide-[#E4D5B7]">
                  {rejectedRecs.slice(0, 3).map((r) => (
                    <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`resubmit-widget-row-${r.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#3F3F3F] truncate">
                          {r.source_location || "—"}
                          {r.reference_number && (
                            <span className="ml-2 text-[11px] text-[#3F3F3F]/50">Ref: {r.reference_number}</span>
                          )}
                        </div>
                        {r.rejection_reason && (
                          <div className="text-xs text-[#A0522D] truncate">Reason: {r.rejection_reason}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => openResubmit(r)}
                        data-testid={`resubmit-widget-edit-${r.id}`}
                        className="text-xs font-medium px-3 h-8 rounded-md border border-[#A0522D] text-[#A0522D] bg-white hover:bg-[#A0522D] hover:text-white transition-colors whitespace-nowrap"
                      >
                        Edit &amp; Resubmit
                      </button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })()}

        {/* Tabs (Task 2): New collection form vs My Records history */}
        <div className="flex items-center gap-1 border-b border-[#D2E4D6]" data-testid="collector-tabs">
          {[
            { v: "new", l: "New collection" },
            { v: "records", l: `My Records (${allRecords.length + draftList.length})` },
          ].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => setTab(t.v)}
              data-testid={`collector-tab-${t.v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.v ? "border-[#056839] text-[#056839]" : "border-transparent text-[#3F3F3F]/70 hover:text-[#056839]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "records" && (
          <MyRecords
            records={allRecords}
            drafts={draftList}
            subTab={recordsSubTab}
            setSubTab={setRecordsSubTab}
            filters={{ filterFrom, filterTo, filterAgent, filterRef }}
            setFilters={{ setFilterFrom, setFilterTo, setFilterAgent, setFilterRef }}
            agents={agents}
            onResume={resumeDraft}
            onDelete={deleteDraftFromList}
            onStartNew={() => setTab("new")}
            onResubmit={openResubmit}
          />
        )}

        {tab === "new" && (draftList.length > 0 || activeDraft) && (
          <Card
            className="border-[#E4D5B7] bg-[#FBF6EB] rounded-xl"
            data-testid="drafts-chooser"
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Save className="w-4 h-4 text-[#A0522D]" />
                  <div className="text-sm font-semibold text-[#000000]">
                    Saved drafts ({draftList.length})
                  </div>
                </div>
                {activeDraft && (
                  <button
                    type="button"
                    onClick={() => { resetForm(); }}
                    data-testid="start-fresh-btn"
                    className="text-xs inline-flex items-center gap-1 text-[#056839] hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Start fresh
                  </button>
                )}
              </div>
              {draftList.length === 0 ? (
                <div className="text-xs text-[#3F3F3F]/70 italic">No saved drafts yet.</div>
              ) : (
                <div className="space-y-2">
                  {draftList.map((d) => {
                    const isActive = activeDraft?.id === d.id;
                    return (
                      <div
                        key={d.id}
                        data-testid={`draft-card-${d.id}`}
                        className={`flex items-start gap-2 p-2.5 rounded-lg border-2 transition-all ${
                          isActive
                            ? "border-[#056839] bg-white shadow-sm"
                            : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#000000] truncate">
                            {d.label || "Untitled draft"}
                            {isActive && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-[#056839] font-semibold">
                                Editing
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#3F3F3F]/70 mt-0.5">
                            Saved {formatDateTime(d.updated_at)}
                          </div>
                        </div>
                        {!isActive && (
                          <button
                            type="button"
                            onClick={() => resumeDraft(d)}
                            data-testid={`resume-draft-${d.id}`}
                            className="text-xs px-2.5 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                          >
                            Resume
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteDraftFromList(d.id)}
                          data-testid={`delete-draft-${d.id}`}
                          className="p-1 rounded-md text-[#A0522D] hover:bg-[#A0522D]/10"
                          title="Discard this draft"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "new" && (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="collector-form">
          {resubmitTarget && (
            <Card className="border-[#A0522D] bg-[#FBEEE3] rounded-xl" data-testid="resubmit-banner">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#A0522D] font-medium">
                      Editing rejected collection
                    </div>
                    <div className="text-sm text-[#3F3F3F] mt-0.5">
                      Update the fields below and click <strong>Resubmit</strong> to send this collection
                      back to the facility for review.
                    </div>
                    {resubmitTarget.rejection_reason && (
                      <div className="text-xs text-[#A0522D] mt-1">
                        Facility rejection reason: <em>{resubmitTarget.rejection_reason}</em>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={cancelResubmit}
                    data-testid="cancel-resubmit-btn"
                    className="text-xs font-medium text-[#A0522D] hover:underline whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
          {/* 1. Reference number (optional) — moved to top per user request */}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">
              1 · Reference number <span className="text-[#3F3F3F]/50 lowercase tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="e.g. delivery order, invoice #, internal ref"
              maxLength={80}
              data-testid="reference-number-input"
              className="collector-input mt-2"
            />
          </div>

          {/* 2. Date + time */}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> 2 · Collection date &amp; time<span className="text-[#A0522D] ml-0.5">*</span>
            </label>
            <input
              type="datetime-local"
              value={pickupAt}
              onChange={(e) => setPickupAt(e.target.value)}
              max={nowLocalIso()}
              required
              data-testid="pickup-at-input"
              className="collector-input mt-2"
            />
          </div>

          {/* 3. Site name / Agent */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">3 · Collection agent / site<span className="text-[#A0522D] ml-0.5">*</span></label>
            {agents.length === 0 ? (
              <div className="space-y-2" data-testid="no-agents-empty-state">
                <p className="text-xs text-gray-500">No agents registered yet.</p>
                <button type="button"
                  onClick={() => { setEditAgentId(null); setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" }); setAgentModalMode("add"); setShowAgentModal(true); }}
                  className="text-sm text-green-700 hover:underline"
                  data-testid="register-first-agent-btn">
                  + Register your first collection agent
                </button>
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <select
                  value={agentId}
                  onChange={e => {
                    const selectedAgent = agents.find(a => a.id === e.target.value);
                    setAgentId(e.target.value);
                    if (selectedAgent?.address) setAddress(selectedAgent.address);
                    else if (!e.target.value) setAddress("");
                  }}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  data-testid="agent-select"
                >
                  <option value="">Select agent…</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.address ? ` — ${a.address}` : ""}</option>
                  ))}
                </select>
                <button type="button"
                  onClick={() => { setEditAgentId(null); setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" }); setAgentModalMode("add"); setShowAgentModal(true); }}
                  className="px-3 py-2 border border-green-600 text-green-700 rounded-lg text-sm hover:bg-green-50 whitespace-nowrap"
                  title="Add new agent"
                  data-testid="add-agent-btn">
                  + New
                </button>
              </div>
            )}
          </div>

          {/* 4. Address */}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">
              4 · Full address<span className="text-[#A0522D] ml-0.5">*</span>
            </label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street, number, district, city…"
              required
              rows={2}
              readOnly={!!agentId}
              data-testid="address-input"
              className={`collector-input mt-2 py-3 leading-snug ${agentId ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
              style={{ minHeight: 64 }}
            />
            {agentId && (
              <p className="text-xs text-gray-400 mt-1">Address from selected agent — edit via agent management</p>
            )}
          </div>

          {/* 5. Facility */}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#69A488] font-medium">
              5 · Destination facility<span className="text-[#A0522D] ml-0.5">*</span>
            </label>
            {facilities.length === 0 ? (
              <div className="collector-input mt-2 flex items-center text-sm text-[#A0522D] bg-[#E4D5B7]/40" data-testid="no-facility-empty-state">
                No facility in your channel yet. Contact your administrator.
              </div>
            ) : facilities.length === 1 ? (
              <div className="collector-input mt-2 flex items-center text-sm text-[#3F3F3F] bg-gray-50" data-testid="facility-locked">
                {facilities[0].name}
                <span className="ml-auto text-[10px] uppercase tracking-wider text-[#69A488]">Auto-assigned</span>
              </div>
            ) : (
              <select
                value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                required
                data-testid="facility-select"
                className="collector-input mt-2"
              >
                <option value="">Select facility…</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 6. Materials (multi-row) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">6 · Materials<span className="text-[#A0522D] ml-0.5">*</span></label>
            {materialRows.map((row, i) => (
              <div key={row._uid || `material-row-${i}`} className="flex gap-2 items-center" data-testid={`material-row-${i}`}>
                <select
                  value={row.material}
                  onChange={e => updateMaterialRow(i, "material", e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  required={i === 0}
                  data-testid={`material-select-${i}`}
                >
                  <option value="">Select material…</option>
                  {MATERIALS.map(m => (
                    <option key={m.v} value={m.v}>{m.label} — {m.desc}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  placeholder="kg"
                  value={row.weight}
                  onChange={e => updateMaterialRow(i, "weight", e.target.value)}
                  className="w-24 border rounded-lg px-3 py-2 text-sm"
                  required={i === 0}
                  data-testid={`weight-input-${i}`}
                />
                {materialRows.length > 1 && (
                  <button type="button" onClick={() => removeMaterialRow(i)} data-testid={`remove-material-${i}`}>
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </div>
            ))}
            {materialRows.length < 6 && (
              <button type="button" onClick={addMaterialRow}
                className="text-sm text-green-700 hover:underline mt-1"
                data-testid="add-material-btn">
                + Add another material
              </button>
            )}
            <div className="text-xs text-gray-400 mt-1" data-testid="materials-total">
              Total: {materialRows
                .filter((r) => r.material && parseFloat(r.weight) > 0)
                .reduce((s, r) => s + parseFloat(r.weight), 0)
                .toFixed(1)} kg
            </div>
          </div>

          {/* 7. Photos (up to 5, mandatory) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              7 · Photos<span className="text-[#A0522D] ml-0.5">*</span> <span className="text-gray-400 font-normal">(up to 5)</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {photos.map(p => (
                <div key={p.idx} className="relative">
                  <img src={p.preview} className="w-24 h-24 object-cover rounded-xl border" alt="preview" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                    </div>
                  )}
                  <div className="text-xs text-gray-400 text-center mt-1 w-24 truncate">
                    {formatTime(p.timestamp)}
                    {p.source === "exif" ? " 📷" : " ⬆"}
                  </div>
                  <button type="button" onClick={() => removePhoto(p.idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                    data-testid={`remove-photo-${p.idx}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-green-400 transition-colors">
                  <Camera className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-400 mt-1">Add photo</span>
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={handlePhotoAdd} data-testid="photo-input" />
                </label>
              )}
            </div>
          </div>

          {/* Transaction (optional) */}
          <div className="space-y-3 pt-4 border-t" data-testid="transaction-block">
            <label className="block text-sm font-medium text-gray-700">
              8 · Transaction <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium text-gray-600">RM</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={txAmount}
                onChange={e => setTxAmount(e.target.value)}
                placeholder="Amount"
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                data-testid="transaction-amount-input"
              />
            </div>
            <div>
              {!proofFilename ? (
                <label className="flex items-center gap-2 text-sm text-green-700 cursor-pointer hover:underline">
                  <span>+ Attach bank transfer / receipt</span>
                  <input type="file" accept=".pdf,image/*" className="hidden"
                    onChange={e => { const f = e.target.files[0]; if (f) { setProofFile(f); uploadProof(f); } }}
                    data-testid="transaction-proof-input" />
                </label>
              ) : (
                <div className="flex items-center gap-2 text-sm" data-testid="transaction-proof-attached">
                  <span className="text-gray-600 truncate">{proofFilename}</span>
                  {proofUploading && <span className="text-xs text-gray-400">Uploading…</span>}
                  {!proofUploading && <span className="text-xs text-green-600">✓</span>}
                  <button type="button" onClick={() => { setProofFile(null); setProofPath(""); setProofFilename(""); }}
                    className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              )}
            </div>
          </div>

          {/* Submit + Save as draft */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={saveDraft}
              disabled={savingDraft || busy || photos.some(p => p.uploading)}
              data-testid="save-draft-btn"
              className="h-14 rounded-xl border-2 border-[#A0522D] text-[#A0522D] font-medium text-sm bg-white hover:bg-[#A0522D]/5 active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center"
            >
              {savingDraft ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Save className="w-4 h-4 mr-1.5" /> Save as draft</>
              )}
            </button>
            <button
              type="submit"
              disabled={busy || photos.some(p => p.uploading)}
              data-testid="submit-collection-btn"
              className="h-14 rounded-xl gfs-bg-primary text-white font-medium text-base shadow-sm hover:bg-[#044E2A] active:scale-[0.99] transition-all disabled:opacity-60 flex items-center justify-center"
            >
              {busy ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <><Package className="w-5 h-5 mr-2" /> {resubmitTarget ? "Resubmit" : "Submit"}</>
              )}
            </button>
          </div>
        </form>
        )}

        {showAgentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="agent-modal">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#D2E4D6] bg-[#F9F8F6]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#056839]/10 flex items-center justify-center">
                    {agentModalMode === "add"
                      ? <Plus className="w-4 h-4 text-[#056839]" />
                      : <Users className="w-4 h-4 text-[#056839]" />}
                  </div>
                  <h3 className="font-semibold text-[#000000]">
                    {agentModalMode === "add"
                      ? (editAgentId ? "Edit collection agent" : "Add new collection agent")
                      : "My collection agents"}
                  </h3>
                </div>
                <button type="button"
                  onClick={() => { setShowAgentModal(false); setEditAgentId(null); setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" }); }}
                  className="w-8 h-8 rounded-full hover:bg-[#D2E4D6]/60 text-[#3F3F3F] flex items-center justify-center transition-colors"
                  data-testid="agent-modal-close">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              {agentModalMode === "add" ? (
                /* ====== ADD/EDIT FORM ONLY (no list) ====== */
                <div className="p-6 overflow-y-auto">
                  <p className="text-sm text-[#3F3F3F]/70 mb-5">
                    Register a collection agent. They&apos;ll be selectable on every collection you log.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      ["name", "Full name", true, "Jane Doe"],
                      ["phone", "Phone", true, "+60 12 345 6789"],
                      ["address", "Address", true, "Street, district, city", true],
                      ["email", "Email", false, "jane@example.com"],
                      ["registration_number", "Registration No.", false, "REG-12345"],
                    ].map(([k, label, required, placeholder, fullWidth]) => (
                      <div key={k} className={fullWidth ? "sm:col-span-2" : ""}>
                        <label className="block text-[10px] uppercase tracking-wider text-[#69A488] font-medium mb-1.5">
                          {label}{required && <span className="text-[#A0522D] ml-0.5">*</span>}
                        </label>
                        <input
                          value={agentForm[k]}
                          onChange={e => setAgentForm(p => ({...p, [k]: e.target.value}))}
                          placeholder={placeholder}
                          className="w-full border border-[#D2E4D6] focus:border-[#056839] focus:ring-1 focus:ring-[#056839] outline-none rounded-lg px-3 py-2.5 text-sm bg-[#F9F8F6]"
                          data-testid={`my-agent-field-${k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* ====== MANAGE MODE — list with inline add + edit ====== */
                <div className="overflow-y-auto flex-1">
                  {/* List */}
                  <div className="p-6 pb-4">
                    {agents.length === 0 ? (
                      <div className="text-center py-10 border-2 border-dashed border-[#D2E4D6] rounded-xl">
                        <Users className="w-8 h-8 text-[#69A488] mx-auto mb-2" />
                        <p className="text-sm text-[#3F3F3F]/70">No agents yet.</p>
                        <p className="text-xs text-[#3F3F3F]/50 mt-1">Add your first one below.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {agents.map(a => {
                          const isEditing = editAgentId === a.id;
                          return (
                            <div
                              key={a.id}
                              className={`p-4 rounded-xl border transition-all ${
                                isEditing
                                  ? "border-[#056839] bg-[#D2E4D6]/30 shadow-sm"
                                  : "border-[#D2E4D6] bg-white hover:border-[#69A488] hover:shadow-sm"
                              }`}
                              data-testid={`my-agent-row-${a.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#056839]/10 text-[#056839] font-semibold flex items-center justify-center text-sm shrink-0">
                                  {(a.name || "?").trim().slice(0,1).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-[#000000] truncate">{a.name}</p>
                                  {a.address && <p className="text-xs text-[#3F3F3F]/70 mt-0.5 line-clamp-2">{a.address}</p>}
                                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#69A488]">
                                    {a.phone && <span>{a.phone}</span>}
                                    {a.email && <span className="truncate max-w-[140px]">{a.email}</span>}
                                    {a.registration_number && <span>#{a.registration_number}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2 mt-3 pt-3 border-t border-[#D2E4D6]/60">
                                <button type="button"
                                  onClick={() => { setEditAgentId(a.id); setAgentForm({ name: a.name, address: a.address || "", phone: a.phone || "", email: a.email || "", registration_number: a.registration_number || "" }); }}
                                  className="flex-1 text-xs text-[#056839] hover:bg-[#D2E4D6]/40 rounded-md py-1.5 font-medium transition-colors"
                                  data-testid={`my-agent-edit-${a.id}`}>Edit</button>
                                <div className="w-px bg-[#D2E4D6]"></div>
                                <button type="button" onClick={() => deleteAgent(a.id)}
                                  className="flex-1 text-xs text-[#A0522D] hover:bg-[#E4D5B7]/40 rounded-md py-1.5 font-medium transition-colors"
                                  data-testid={`my-agent-remove-${a.id}`}>Remove</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Inline add/edit form */}
                  <div className="border-t border-[#D2E4D6] bg-[#F9F8F6] p-6 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1 h-4 bg-[#056839] rounded"></div>
                      <p className="text-xs font-semibold text-[#056839] uppercase tracking-wider">
                        {editAgentId ? "Edit agent" : "Add a new agent"}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        ["name", "Full name", true, "Jane Doe"],
                        ["phone", "Phone", true, "+60 12 345 6789"],
                        ["address", "Address", true, "Street, district, city", true],
                        ["email", "Email", false, "jane@example.com"],
                        ["registration_number", "Registration No.", false, "REG-12345"],
                      ].map(([k, label, required, placeholder, fullWidth]) => (
                        <div key={k} className={fullWidth ? "sm:col-span-2" : ""}>
                          <label className="block text-[10px] uppercase tracking-wider text-[#69A488] font-medium mb-1">
                            {label}{required && <span className="text-[#A0522D] ml-0.5">*</span>}
                          </label>
                          <input
                            value={agentForm[k]}
                            onChange={e => setAgentForm(p => ({...p, [k]: e.target.value}))}
                            placeholder={placeholder}
                            className="w-full border border-[#D2E4D6] focus:border-[#056839] focus:ring-1 focus:ring-[#056839] outline-none rounded-lg px-3 py-2 text-sm bg-white"
                            data-testid={`my-agent-field-${k}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="border-t border-[#D2E4D6] px-6 py-4 bg-white flex gap-2">
                {editAgentId && (
                  <button type="button"
                    onClick={() => { setEditAgentId(null); setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" }); }}
                    className="px-4 py-2 text-sm border border-[#D2E4D6] rounded-lg text-[#3F3F3F] hover:bg-[#F9F8F6]"
                    data-testid="my-agent-cancel">Cancel</button>
                )}
                <div className="flex-1"></div>
                {agentModalMode === "add" && (
                  <button type="button"
                    onClick={() => { setShowAgentModal(false); setEditAgentId(null); setAgentForm({ name: "", address: "", phone: "", email: "", registration_number: "" }); }}
                    className="px-4 py-2 text-sm border border-[#D2E4D6] rounded-lg text-[#3F3F3F] hover:bg-[#F9F8F6]"
                    data-testid="my-agent-close-btn">Close</button>
                )}
                <button type="button"
                  onClick={async () => {
                    await saveAgent();
                    if (agentModalMode === "add") setShowAgentModal(false);
                  }}
                  disabled={!agentForm.name || !agentForm.address || !agentForm.phone || savingAgent}
                  className="px-5 py-2 text-sm bg-[#056839] hover:bg-[#045530] text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="my-agent-save">
                  {savingAgent ? "Saving…" : editAgentId ? "Update agent" : "Save agent"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Success overlay */}
      {success && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center gfs-bg-primary"
          data-testid="success-overlay"
        >
          <div className="text-center text-white animate-success-pulse">
            <div className="w-24 h-24 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-4">
              <Check className="w-14 h-14 stroke-[3]" />
            </div>
            <div className="font-heading text-3xl font-semibold tracking-tight">
              Submitted
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Pending: "bg-[#E4D5B7] text-[#A0522D]",
    Received: "bg-[#D2E4D6] text-[#056839]",
    Rejected: "bg-[#A0522D]/10 text-[#A0522D]",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${map[status]}`}
      data-testid={`status-badge-${status.toLowerCase()}`}
    >
      {status}
    </span>
  );
}
